import type { MissionAssessmentDraftListItemDto } from '@@types/api.types';
import { UnsavedChangesModal } from '@components/shared/unsaved-changes-modal.component';
import { Button } from '@components/ui/button';
import { withForm } from '@components/ui/form/form';
import { useStore } from '@tanstack/react-form';
import { formatVersionDate } from '@utils/date.util';
import { formatShortUuid } from '@utils/text.util';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { missionAssessmentDraftFormOptions } from './mission-assessment-form-options';

export const MissionAssessmentDraftNavigation = withForm({
  ...missionAssessmentDraftFormOptions,
  props: {
    currentDraftId: undefined as string | undefined,
    isTransitioning: undefined as boolean | undefined,
    technologyId: undefined as string | undefined,
    versions: undefined as MissionAssessmentDraftListItemDto[] | undefined,
  },
  render: function Render(renderProps) {
    const { currentDraftId, isTransitioning = false, technologyId, versions, form } = renderProps;

    const navigate = useNavigate();
    const [clickedDirection, setClickedDirection] = useState<'previous' | 'next' | null>(null);
    const [pendingDirection, setPendingDirection] = useState<'previous' | 'next' | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const hasUnsavedChanges = useStore(form.store, (state) => state.isDirty);

    useEffect(() => {
      if (currentDraftId) {
        setClickedDirection(null);
        setPendingDirection(null);
        setIsModalOpen(false);
        setIsSaving(false);
      }
    }, [currentDraftId]);

    const currentIndex = useMemo(
      () => (versions ? versions.findIndex((v) => v.id === currentDraftId) : -1),
      [versions, currentDraftId],
    );
    if (!versions || versions.length === 0 || currentIndex === -1) {
      return <></>;
    }

    const currentVersion = versions[currentIndex];
    const hasMultipleVersions = versions.length > 1;
    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex < versions.length - 1;

    const getTargetVersionId = (direction: 'previous' | 'next'): string | null => {
      if (direction === 'previous' && hasPrevious) return versions[currentIndex - 1].id;
      if (direction === 'next' && hasNext) return versions[currentIndex + 1].id;
      return null;
    };

    const proceedNavigation = (direction: 'previous' | 'next') => {
      const targetId = getTargetVersionId(direction);
      if (!targetId) return;
      setClickedDirection(direction);
      form.reset();
      navigate(`/technology/${technologyId}/assessment/${targetId}/edit?tab=ma`, { replace: true });
    };

    const handleNavigate = (direction: 'previous' | 'next') => {
      const canNavigate = direction === 'previous' ? hasPrevious : hasNext;
      if (!canNavigate) return;

      if (hasUnsavedChanges) {
        setPendingDirection(direction);
        setIsModalOpen(true);
        return;
      }
      proceedNavigation(direction);
    };

    const handleNavigateToPrevious = () => handleNavigate('previous');
    const handleNavigateToNext = () => handleNavigate('next');

    const handleCancel = () => {
      setIsModalOpen(false);
      setPendingDirection(null);
    };

    const handleDiscard = () => {
      if (!pendingDirection) return;
      setIsModalOpen(false);
      proceedNavigation(pendingDirection);
    };

    const handleSaveAndContinue = async () => {
      if (!pendingDirection) return;
      setIsSaving(true);
      setClickedDirection(pendingDirection);
      try {
        await form.handleSubmit({ submitAction: 'draft' });
        setIsModalOpen(false);
        proceedNavigation(pendingDirection);
      } catch (e) {
        setClickedDirection(null);
        setIsModalOpen(true);
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <div className="sticky top-0 z-10 bg-white py-4 flex-1 px-4">
        <div className="flex w-full items-start justify-between gap-4">
          {/* Left column: Draft version info */}
          <div className="flex-1 flex flex-col">
            <div className="text-xs text-gray-700">Draft {formatShortUuid(currentVersion.version)} </div>
            <div className="text-xs text-gray-700">Last updated on {formatVersionDate(currentVersion.updatedAt)}</div>
          </div>

          {/* Right column: Navigation controls */}
          {hasMultipleVersions && (
            <div className="flex items-center gap-2">
              <Button
                disabled={!hasPrevious || clickedDirection !== null || isTransitioning}
                onClick={handleNavigateToPrevious}
                size="sm"
                variant="secondary"
              >
                {clickedDirection === 'previous' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ChevronLeft className="h-4 w-4" />
                )}
              </Button>
              <span className="text-xs text-gray-500 min-w-12 text-center">
                {currentIndex + 1} of {versions.length}
              </span>
              <Button
                disabled={!hasNext || clickedDirection !== null || isTransitioning}
                onClick={handleNavigateToNext}
                size="sm"
                variant="secondary"
              >
                {clickedDirection === 'next' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </div>
          )}
        </div>

        <UnsavedChangesModal
          isOpen={isModalOpen}
          isSaving={isSaving}
          onCancel={handleCancel}
          onDiscard={handleDiscard}
          onSaveAndContinue={handleSaveAndContinue}
        />
      </div>
    );
  },
});
